import { getLocalStorageItem, setLocalStorageItem } from '@/utils'
import { isEmpty, isNil } from 'ramda'
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react'
import {
  currentStepLocalStorageKey,
  formValuesLocalStorageKey,
  makeLocalStorageKey,
} from '../helpers/makeLocalStorageKey'

interface StepperContextType {
  currentStep: number
  onNext: (key?: string, values?: any) => void
  onPrevious: () => void
  formValues: Record<string, any>
  isLast: boolean
}

interface StepperProviderProps {
  children: ReactNode
  defaultValues: Record<string, any>
  locationId: string
  steps: Record<string, any>
  onFinish?: () => void
}

const StepperContext = createContext<StepperContextType | undefined>(undefined)

export const useStepper = () => {
  const context = useContext(StepperContext)
  if (!context) {
    throw new Error('useStepper must be used within a StepperProvider')
  }
  return context
}

export const StepperProvider = ({
  children,
  defaultValues,
  locationId,
  steps,
  onFinish,
}: StepperProviderProps) => {
  const [currentStep, setCurrentStep] = useState(() => {
    const savedStep = localStorage.getItem(
      makeLocalStorageKey(currentStepLocalStorageKey, locationId),
    )
    return savedStep ? JSON.parse(savedStep).currentStep : 0
  })

  const isLast = Object.keys(steps).length - 1 === currentStep

  const [formValues, setFormValues] = useState(() => {
    const values = getLocalStorageItem(
      makeLocalStorageKey(formValuesLocalStorageKey, locationId),
    )

    if (!isEmpty(values) || !isNil(values)) {
      return { ...defaultValues, ...values }
    }
    return defaultValues
  })

  useEffect(() => {
    setLocalStorageItem(`yearlyAssessmentCurrentStep-${locationId}`, {
      currentStep,
    })
  }, [currentStep, locationId])

  useEffect(() => {
    setLocalStorageItem(`yearlyAssessmentFormValues-${locationId}`, formValues)
  }, [formValues, locationId])

  const onNext = (key?: string, values?: any) => {
    if (!isLast) {
      if (key && values) {
        setFormValues((prev: Record<string, any>) => ({
          ...prev,
          [key]: values,
        }))
      }
      setCurrentStep((prev: number) => prev + 1)
    } else {
      onFinish?.()
    }
  }

  const onPrevious = () =>
    currentStep > 0 ? setCurrentStep((prev: number) => prev - 1) : null

  return (
    <StepperContext.Provider
      value={{ currentStep, onNext, onPrevious, formValues, isLast }}
    >
      {children}
    </StepperContext.Provider>
  )
}
